/**
 * The silent-microphone watchdog (2026-09-21). A line whose microphone
 * delivers nothing used to sit deaf for a minute and never say so. The line
 * now tries another input by itself, says so when no input has signal, and
 * recovers the moment signal appears. A healthy microphone is looked at until
 * its first non-zero sample (a look or two: the analyser's first read is
 * empty) and then costs nothing.
 *
 * DEAD IS NOT QUIET. A quiet room is tiny non-zero floats; a dead input is
 * exact zeros. So the read is the FLOAT time-domain data, never the 8-bit
 * bytes (a quiet room quantises to 128 there and looks dead). A track that
 * has produced any non-zero sample is alive for good: after that only the
 * track itself going `muted` or `ended` can declare it dead again.
 *
 * TWO KINDS OF EVIDENCE. HARD is the track itself saying the source delivers
 * nothing (`muted`, `ended`): dead after MIC_DEAD_WINDOW_MS, swapped at once.
 * SOFT is exact zeros alone, which a hard-gated microphone also reads while
 * its owner is merely listening: dead only when the window has run AND the
 * line has been on the air MIC_SAY_AFTER_MS. So zeros alone never swap a
 * microphone inside the first eight seconds, and never call it silent either;
 * past the floor every window is the short one again.
 *
 * Pure logic and a tiny runner. The client (liveClient.ts) owns the track,
 * the analyser and the swap; this leaf owns the verdict and the numbers.
 */
import { LINE_SPEECH_RMS } from "@bash/shared";

import {
  isBuiltIn,
  isContinuityPhone,
  micDeviceClass,
  type AudioInputInfo,
  type MicDeviceClass,
} from "@/lib/voice/mic";

/** Continuous dead this long, from the moment the line is live, is dead. */
export const MIC_DEAD_WINDOW_MS = 2_500;
export const MIC_POLL_MS = 250;
/** Each swap can cost a Bluetooth profile switch: two a call, then it says so. */
export const MIC_MAX_SWAPS = 2;
/**
 * On zeros alone nothing is SAID and nothing is SWAPPED before the line has
 * been on the air this long (lead ruling 2026-09-21). A hard-gated microphone
 * (macOS Voice Isolation, Krisp, some USB mics) reads exact zeros while its
 * owner is merely listening to the greeting, which no look can tell from a
 * dead device. A swap is not free either: it can drop a pair of earbuds from
 * A2DP to HFP, so the floor guards the swap too. Only the track's own `muted`
 * or `ended` is trusted before it. The deaf calls this guards against ran 30
 * to 70 seconds.
 */
export const MIC_SAY_AFTER_MS = 8_000;
/**
 * The evidence line (liveClient.ts): one picture of the mic and the uplink,
 * this long after the line is live. FIFTEEN seconds, because the picture is
 * worth nothing until the verdict has settled: on zeros alone the worst case
 * is a first swap at MIC_SAY_AFTER_MS, a second about 10.75 s in and the
 * sentence about 13.5 s in, so a shorter wait would photograph a dead mic
 * mid-flight and log `micDead: false`. A call the person ends before this
 * still leaves its evidence at the close (flushMicStats), as it always did.
 */
export const MIC_EVIDENCE_MS = 15_000;
/**
 * How often the evidence line asks the peer for its own level of the sent track. A poll of
 * `getStats()` costs more than a float read, and a voice that carries reaches its peak many times
 * a second, so four times slower than the analyser is plenty to catch it.
 */
export const MIC_LEVEL_POLL_MS = 1_000;
/** Under this a float sample is digital zero. */
const MIC_ZERO = 1e-7;

export function hasSignal(samples: Float32Array): boolean {
  for (let i = 0; i < samples.length; i++)
    if (Math.abs(samples[i]) >= MIC_ZERO) return true;
  return false;
}

/**
 * SPEECH LEVEL, not loudness (2026-09-21, the deaf calls). `micPeak` is the largest single sample
 * of a window, so a chair scrape reads 1.000 on a call in which nobody said a word — his six deaf
 * calls read 0.053 to 1.000 with no pattern, and the log could not say whether his VOICE was ever
 * in the stream. RMS per poll answers that: how loud the window's loud part was, and for how long
 * it carried. Numbers only, never audio, exactly as the rest of the evidence line.
 */
export function rmsOf(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}

export interface SpeechLevel {
  /** The 90th percentile of the window's per-poll RMS, in thousandths. */
  rms: number;
  /** Total ms of polls whose RMS stood at or above LINE_SPEECH_RMS. */
  voicedMs: number;
}

export interface SpeechMeter {
  /** One poll's float samples. */
  add(samples: Float32Array): void;
  /** The whole call so far, for the evidence line. Reading it does not end it. */
  read(): SpeechLevel;
  /** The greeting ended here: what the heal counts from. A second mark replaces the first. */
  mark(): void;
  /** Since the mark, or the whole window while there is none. */
  since(): SpeechLevel;
  /**
   * The unbroken run of non-voiced polls since his last voiced one, or null while he has never
   * been voiced at all. The heal waits on this: mid-sentence the vendor owes us no transcript.
   */
  quietMs(): number | null;
}

/**
 * The window's speech level, one poll at a time. The 90th PERCENTILE, not the max: one loud poll
 * in a quiet window is a cough, and the max would call that a conversation; nearest-rank keeps the
 * loud tenth of a window that really is speech. `voicedMs` counts polls, not clock, so a paused
 * tab (whose timer never fires) never invents time somebody was talking.
 */
export function startSpeechMeter(pollMs: number = MIC_POLL_MS): SpeechMeter {
  const polls: number[] = [];
  let markedAt = 0;
  let lastVoiced = -1;
  const levelOf = (from: number): SpeechLevel => {
    const window = polls.slice(from);
    if (window.length === 0) return { rms: 0, voicedMs: 0 };
    const sorted = [...window].sort((a, b) => a - b);
    const rank = Math.ceil(0.9 * sorted.length) - 1;
    return {
      rms: Math.round(sorted[rank] * 1000) / 1000,
      voicedMs: window.filter((r) => r >= LINE_SPEECH_RMS).length * pollMs,
    };
  };
  return {
    add: (samples) => {
      const rms = rmsOf(samples);
      if (rms >= LINE_SPEECH_RMS) lastVoiced = polls.length;
      polls.push(rms);
    },
    read: () => levelOf(0),
    mark: () => {
      markedAt = polls.length;
    },
    since: () => levelOf(markedAt),
    // Counted from the MARK, so a swapped input starts its own run: what he
    // said into the input that could not carry him is not this one's silence.
    quietMs: () => {
      const last = lastVoiced >= markedAt ? lastVoiced : -1;
      return last < 0 ? null : (polls.length - 1 - last) * pollMs;
    },
  };
}

export interface MicObservation {
  /** The person pressed Mute (`track.enabled` false): the watchdog sleeps. */
  offAir: boolean;
  /** `track.muted`: the source is delivering nothing. */
  muted: boolean;
  /** `track.readyState` is "ended". */
  ended: boolean;
  /** This read held a non-zero sample. */
  signal: boolean;
}

export interface MicWatchState {
  /** A non-zero sample was seen on this track. Never goes back. */
  alive: boolean;
  /** When the unbroken run of dead looks began, or null. */
  deadSince: number | null;
}

export const FRESH_MIC: MicWatchState = { alive: false, deadSince: null };

export type MicVerdict = "asleep" | "watching" | "alive" | "dead";

/**
 * One look at the microphone. `softOk` is the caller's word that zeros alone
 * may convict now (the line has been on the air MIC_SAY_AFTER_MS); hard
 * evidence never needs it.
 */
export function stepMicWatch(
  s: MicWatchState,
  o: MicObservation,
  now: number,
  softOk = true
): { state: MicWatchState; verdict: MicVerdict } {
  // A muted line is never dead, and the window starts over when it lifts.
  if (o.offAir) return { state: { ...s, deadSince: null }, verdict: "asleep" };
  const alive = s.alive || o.signal;
  const hard = o.muted || o.ended;
  if (alive && !hard)
    return { state: { alive, deadSince: null }, verdict: "alive" };
  const deadSince = s.deadSince ?? now;
  return {
    state: { alive, deadSince },
    verdict:
      now - deadSince >= MIC_DEAD_WINDOW_MS && (hard || softOk)
        ? "dead"
        : "watching",
  };
}

/** What a track says about the device behind it (`getSettings()`, `label`). */
export interface MicIdentity {
  deviceId?: string;
  groupId?: string;
  label?: string;
}

const ALIASES = new Set(["default", "communications"]);

/**
 * The inputs worth swapping to, in order: the built-in first, then the other
 * real inputs as the browser lists them. Never an alias row, never a
 * Continuity phone, never a device this call already used. A used "default"
 * or "communications" is resolved to the device behind it by groupId, or by
 * label ("Default - MacBook Pro Microphone") where the browser gives none.
 */
export function micSwapCandidates(
  devices: AudioInputInfo[],
  used: MicIdentity[],
  prefer?: MicDeviceClass | null
): string[] {
  const inputs = devices.filter((d) => d.kind === "audioinput");
  const resolved = used.map((u) => {
    if (!ALIASES.has(u.deviceId ?? "")) return u;
    const row = inputs.find((d) => d.deviceId === u.deviceId);
    return {
      deviceId: u.deviceId,
      groupId: u.groupId || row?.groupId,
      label: (u.label || row?.label || "").replace(/^.*? - /, ""),
    };
  });
  const isUsed = (d: AudioInputInfo) =>
    resolved.some(
      (u) =>
        d.deviceId === u.deviceId ||
        (!!d.groupId && d.groupId === u.groupId) ||
        (!!d.label && d.label === u.label)
    );
  const real = inputs.filter(
    (d) =>
      d.deviceId &&
      !ALIASES.has(d.deviceId) &&
      !isContinuityPhone(d.label) &&
      !isUsed(d)
  );
  // The DEAF heal asks for a class (2026-09-21): a mic whose owner it cannot
  // carry is not healed by the next mic of the same kind, so it names the other
  // one and that class goes first. The silent-mic watchdog passes none and gets
  // the order it always had, built-in first.
  if (prefer) {
    const wanted = real.filter((d) => micDeviceClass(d.label) === prefer);
    const rest = real.filter((d) => micDeviceClass(d.label) !== prefer);
    return [...wanted, ...rest].map((d) => d.deviceId);
  }
  return [
    ...real.filter((d) => isBuiltIn(d.label)),
    ...real.filter((d) => !isBuiltIn(d.label)),
  ].map((d) => d.deviceId);
}

/** The class to try when THIS one cannot carry him: the other side of his two real inputs. */
export function otherMicClass(now: MicDeviceClass): MicDeviceClass {
  return now === "builtin" ? "bluetooth" : "builtin";
}

export interface MicWatchDeps {
  observe(): MicObservation;
  /** Ordered device ids worth trying (micSwapCandidates over the live list). */
  candidates(): Promise<string[]>;
  /** Put this input on the air in place of the dead one; false when it would not take. */
  swapTo(deviceId: string): Promise<boolean>;
  /** No input has signal (true), or signal came back (false). Once per change. */
  onSilent(silent: boolean): void;
}

export interface MicWatch {
  /** The track fired `mute` or `ended`: look again. */
  wake(): void;
  /** The input on the air is judged dead right now (its window is up). For the evidence line. */
  dead(): boolean;
  stop(): void;
}

/**
 * Looks at once (the moment the line is live) and then every MIC_POLL_MS
 * until the track is alive; then it rests, with no timer, until `wake`.
 */
export function startMicWatch(deps: MicWatchDeps): MicWatch {
  let state = FRESH_MIC;
  let verdict: MicVerdict = "watching";
  let timer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  let swapping = false;
  let swaps = 0;
  let silent = false;
  /** No input is left to try: from here it only waits for the say floor. */
  let exhausted = false;
  const startedAt = Date.now();

  const rest = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };
  const say = (next: boolean) => {
    if (silent === next) return;
    silent = next;
    deps.onSilent(next);
  };
  /** The line has been on the air long enough for zeros alone to count. */
  const pastFloor = (now: number) => now - startedAt >= MIC_SAY_AFTER_MS;
  const sayWhenDue = () => {
    if (pastFloor(Date.now())) say(true);
  };
  const swap = async () => {
    if (exhausted) return sayWhenDue();
    swapping = true;
    try {
      // A list that will not read is nothing to try, the same as an empty one.
      const id =
        swaps < MIC_MAX_SWAPS
          ? (await deps.candidates().catch(() => []))[0]
          : undefined;
      if (stopped) return;
      if (id === undefined) {
        exhausted = true;
        return sayWhenDue();
      }
      // An input that would not open spends a swap too: the cap bounds the work.
      swaps += 1;
      if (await deps.swapTo(id).catch(() => false)) {
        state = FRESH_MIC;
        verdict = "watching";
      }
    } finally {
      swapping = false;
    }
  };
  const look = () => {
    if (stopped || swapping) return;
    const now = Date.now();
    const r = stepMicWatch(state, deps.observe(), now, pastFloor(now));
    state = r.state;
    verdict = r.verdict;
    if (verdict === "alive") {
      say(false);
      rest();
    } else if (verdict === "dead" && !silent) void swap();
  };
  const wake = () => {
    if (stopped || timer) return;
    timer = setInterval(look, MIC_POLL_MS);
    look();
  };
  wake();
  return {
    wake,
    dead: () => verdict === "dead",
    stop: () => {
      stopped = true;
      rest();
    },
  };
}
